/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at Wed Jan 17 2024
 **/

import styled from "@emotion/styled";

import { Column, Extensions, Image, Row, Typography } from "@/frameworks/designStuff/components";
import { useProperties } from "@/frameworks/libraries";
import logoSrc from "@/assets/images/logo/logo_rfice.svg";

function validate_title(title: unknown, url: string) {
	return typeof title === "string" ? title : `${url}`;
}

export default function Link({
	url,
	title,
	info,
	isFile = false,
	nextElement
}: ChatServices.Format2.Elements.Link & {
	isFile?: boolean;
	nextElement?: ChatServices.Format2.Elements.SectionAllowElements;
}) {
	const isUrlValid = (urlCheck: string) => {
		const res = urlCheck.match(
			/(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-zA-Z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)/g
		);
		return !!res;
	};

	return (
		<>
			{!isFile && isUrlValid(url) && (
				<a href={url} target="_blank">
					{nextElement?.type === "link" && !isUrlValid(nextElement.url)
						? `${validate_title(title, url)}${validate_title(nextElement.title, nextElement.url)}`
						: validate_title(title, url)}
				</a>
			)}
			{isFile && info && info.image && <OGTagCard {...info} />}
		</>
	);
}

function OGTagCard(info: ChatServices.Format2.Elements.Link["info"]) {
	const properties = useProperties();

	return (
		<CardWrapper href={info.url} target="_blank" className="og-tag">
			<LinkContainer horizonAlign="center" verticalAlign="center">
				<LinkImage
					src={
						info.image
							? `${properties.default.webDomain}/resize?sourceUrl=${encodeURIComponent(info.image)}`
							: ""
					}
					alt="og-tag"
					errorSrc={logoSrc}
				/>
				<ExtensionBadge>
					<Extensions type={"link"} />
				</ExtensionBadge>
			</LinkContainer>
			{info.image && (
				<>
					<Typography typoSize="Title3">{info.title}</Typography>
					<Typography typoSize="Body5" typoColor="neutral_60_100">
						{info.description}
					</Typography>
					<Typography typoSize="Body4" typoColor="neutral_60_100">
						{info.url}
					</Typography>
				</>
			)}
		</CardWrapper>
	);
}

const CardWrapper = styled(Column.a)`
	border: 1px solid ${({ theme }) => theme.colors.neutral_30_100};
	border-radius: 8px;
	background-color: ${({ theme }) => theme.colors.neutral_00_68};
	padding: 8px 10px 6px 10px;
	width: 182px;
	text-decoration: none !important;
	cursor: pointer;
	span {
		width: 100%;
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
		text-align: start;
	}
`;

const LinkContainer = styled(Column)`
	position: relative;
	width: 100%;
	height: 108px;
	border-radius: 8px;
	background: ${({ theme }) => theme.colors.accent_10_100};
	margin-bottom: 6px;
`;

const LinkImage = styled(Image)`
	width: 100%;
	height: 100%;
	border-radius: 8px;
	background-image: url(${({ src }) => src});
	background-repeat: no-repeat;
	background-size: cover;
	background-position: center;
	object-fit: cover;
	cursor: pointer;
`;

const ExtensionBadge = styled(Row)`
	position: absolute;
	right: 4px;
	top: 4px;
`;
